import { useEffect, useState, useCallback } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import type { components } from "@dolfin/api-public";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { CreditNotesTable } from "@/components/CreditNotesTable";
import { ErrorState, Pager } from "@/components/PageUi";

type CreditNoteResponse = components["schemas"]["CreditNoteResponse"];
type CustomerResponse = components["schemas"]["CustomerResponse"];

const PAGE_SIZE = 10;
const ALL_CUSTOMERS = "__all__";

export interface CreditNotesListPageProps {
  onInvoiceClick: (invoiceId: string) => void;
  /** Opens the credit note itself; falls back to its invoice when not supplied. */
  onCreditNoteClick?: (creditNoteId: string) => void;
  defaultCustomerId?: string;
  onCustomerFilterChange?: (customerId: string | undefined) => void;
}

export function CreditNotesListPage({
  onInvoiceClick,
  onCreditNoteClick,
  defaultCustomerId,
  onCustomerFilterChange,
}: CreditNotesListPageProps) {
  const api = useDolfinClient();

  const [creditNotes, setCreditNotes] = useState<CreditNoteResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [customerId, setCustomerId] = useState<string>(defaultCustomerId ?? ALL_CUSTOMERS);

  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // Follow a deep-linked customer that changes without a remount (back/forward).
  useEffect(() => {
    setCustomerId(defaultCustomerId ?? ALL_CUSTOMERS);
  }, [defaultCustomerId]);

  // Capped: the dropdown is a filter, not a directory.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.GET("/v1/customers", { params: { query: { Limit: 100 } } });
      if (!cancelled) setCustomers(data?.data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const fetchCreditNotes = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, string | number> = { Limit: PAGE_SIZE };
        if (customerId !== ALL_CUSTOMERS) query.CustomerId = customerId;
        if (cursor) query.Cursor = cursor;

        const { data, error: apiError } = await api.GET("/v1/credit-notes", {
          params: { query },
        });

        if (apiError || !data) {
          console.error("Failed to load credit notes:", apiError);
          setError("Failed to load credit notes");
          return;
        }

        setCreditNotes(data.data ?? []);
        setNextCursor(data.meta?.cursors?.next ?? null);
      } catch (e) {
        console.error("Failed to load credit notes:", e);
        setError("Failed to load credit notes");
      } finally {
        setLoading(false);
      }
    },
    [api, customerId],
  );

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchCreditNotes();
  }, [fetchCreditNotes]);

  function handleCustomerChange(value: string) {
    setCustomerId(value);
    onCustomerFilterChange?.(value === ALL_CUSTOMERS ? undefined : value);
  }

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchCreditNotes(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setCursorStack(newStack);
    fetchCreditNotes(prevCursor);
  }

  const currentPage = cursorStack.length + 1;

  const filtered = customerId !== ALL_CUSTOMERS;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Receivables"
        title="Credit notes"
        subtitle="Credits raised against your invoices, with their net and tax breakdown."
      />

      {/* Only worth a filter once there is more than one customer to tell apart. */}
      {customers.length > 1 || filtered ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Select value={customerId} onValueChange={handleCustomerChange}>
            <SelectTrigger className="h-9 w-full sm:w-[240px]" aria-label="Filter by customer">
              <SelectValue placeholder="All customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CUSTOMERS}>All customers</SelectItem>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id!}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {error ? (
        <ErrorState
          message="Credit notes could not be loaded."
          onRetry={() => fetchCreditNotes(cursorStack[cursorStack.length - 1])}
        />
      ) : (
        <>
          <CreditNotesTable
            creditNotes={creditNotes}
            loading={loading}
            emptyMessage={filtered ? "No credit notes for this customer" : "No credit notes yet"}
            emptyAction={
              filtered ? (
                <Button variant="outline" size="sm" onClick={() => handleCustomerChange(ALL_CUSTOMERS)}>
                  Show all customers
                </Button>
              ) : undefined
            }
            onRowClick={(cn) =>
              onCreditNoteClick && cn.id ? onCreditNoteClick(cn.id) : cn.invoiceId && onInvoiceClick(cn.invoiceId)
            }
          />

          <Pager
            page={currentPage}
            hasPrevious={currentPage > 1}
            hasNext={!!nextCursor}
            onPrevious={handlePrevPage}
            onNext={handleNextPage}
          />
        </>
      )}
    </PageContainer>
  );
}
